import { type FC, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import * as UpChunk from '@mux/upchunk';
import { api } from '../../helpers/apiUtils';
import { AngebotDialog, AngebotLageChip, lageFehlt, type AngebotLage } from './AngebotDialog';
import { GefahrBestaetigung, NurAdmin, useIstAdmin } from '../../components/marketingForm';
import { TECHNIK_KONTAKT } from './texteLive';

interface AdVideo {
  id: number;
  title: string;
  /** 'ad' = Werbevideo nach der Sendung, 'show' = Aufzeichnung, die als Sendung läuft. Bestand = 'ad'. */
  kind?: 'ad' | 'show';
  cta_count?: number;
  /** Wird ein Knopf sichtbar? null bei Aufzeichnungen. Aus GET /admin/ad-videos. */
  angebot_lage?: AngebotLage | null;
  provider: string;
  mux_upload_id: string | null;
  mux_asset_id: string | null;
  mux_playback_id: string | null;
  hls_url: string | null;
  duration_sec: number | null;
  aspect_ratio: string | null;
  resolution_tier: string | null;
  status: 'uploading' | 'preparing' | 'ready' | 'errored' | 'deleted';
  status_error: string | null;
  is_default: number;
  is_active: number;
  created_by_email: string | null;
  created_at: string | Date;
  updated_at: string | Date;
  /** Vom Wiederholungs-Abgleich angelegt (Quelle). Fehlt vor der Migration. */
  managed_source_webinar_id?: string | null;
  /** Sendetag, fuer den diese Zeile der Auto-Roll ist (YYYY-MM-DD bzw. ISO). */
  rerun_current_for?: string | null;
}

const POLL_MS = 5000;

// Minuten:Sekunden, ab einer Stunde Stunden:Minuten:Sekunden — dieselbe Schreibweise wie im Player.
function fmtDuration(sec: number | null): string {
  if (sec == null || !Number.isFinite(sec)) return '–';
  const ganz = Math.max(0, Math.round(sec));
  const h = Math.floor(ganz / 3600);
  const m = Math.floor((ganz % 3600) / 60);
  const s = ganz % 60;
  const zwei = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${zwei(m)}:${zwei(s)}` : `${m}:${zwei(s)}`;
}

/**
 * Zeit-Eingabe im Knopf-Dialog: „2:30“ = 2 Minuten 30 Sekunden, „1:02:30“ mit Stunden, eine
 * blanke Zahl = ganze Minuten („5“ = 5:00). Persona-Runde 1: „ab Sekunde 2551“ rechnet niemand
 * im Kopf um. Gibt NaN zurück, wenn die Eingabe keine Zeit ist.
 */
function parseMinSek(raw: string): number {
  const v = raw.trim();
  if (!v) return NaN;
  if (/^\d+$/.test(v)) return parseInt(v, 10) * 60;
  const teile = v.split(':');
  if (teile.length < 2 || teile.length > 3 || teile.some((t) => !/^\d+$/.test(t.trim()))) {
    return NaN;
  }
  const n = teile.map((t) => parseInt(t, 10));
  // Sekunden (und bei h:mm:ss die Minuten) müssen unter 60 liegen, sonst ist es ein Tippfehler.
  if (n[n.length - 1] > 59) return NaN;
  if (n.length === 3) {
    if (n[1] > 59) return NaN;
    return n[0] * 3600 + n[1] * 60 + n[2];
  }
  return n[0] * 60 + n[1];
}

/** Hochladedatum als TT.MM.JJJJ (Berliner Kalendertag). */
function fmtDatum(wert: string | Date | null | undefined): string {
  if (!wert) return '–';
  const d = new Date(wert);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleDateString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Eingabefeld für eine Zeit in Minuten:Sekunden. Hält den getippten Text lokal, damit „2:“ beim
 * Tippen nicht sofort zurückspringt; übernommen wird nur ein gültiger Wert. Ungültige Eingaben
 * meldet `onUngueltig`, damit der Dialog das Speichern sperrt statt still den alten Wert zu
 * speichern.
 */
const ZeitFeld: FC<{
  label: string;
  wert: number | null;
  leerErlaubt?: boolean;
  leerText?: string;
  onWert: (sek: number | null) => void;
  onUngueltig: (ungueltig: boolean) => void;
}> = ({ label, wert, leerErlaubt, leerText, onWert, onUngueltig }) => {
  const [text, setText] = useState(wert == null ? '' : fmtDuration(wert));
  const [fehler, setFehler] = useState(false);
  const [fokus, setFokus] = useState(false);
  // Von außen geänderter Wert (z. B. neue Zeile vorbelegt) — nur übernehmen, solange niemand tippt.
  useEffect(() => {
    // Eine ungültige Eingabe bleibt stehen (rot, Speichern gesperrt) — sonst zeigte das Feld still
    // wieder den alten Wert, während der Dialog wegen des Fehlers weiter sperrt.
    if (!fokus && !fehler) setText(wert == null ? '' : fmtDuration(wert));
  }, [wert, fokus, fehler]);
  return (
    <TextField
      label={label}
      size="small"
      value={text}
      placeholder={leerErlaubt ? leerText : 'M:SS'}
      error={fehler}
      helperText={fehler ? 'Form M:SS, z. B. 2:30' : ' '}
      onFocus={() => setFokus(true)}
      onBlur={() => {
        setFokus(false);
        if (!fehler) setText(wert == null ? '' : fmtDuration(wert));
      }}
      onChange={(e) => {
        const roh = e.target.value;
        setText(roh);
        if (roh.trim() === '' && leerErlaubt) {
          setFehler(false);
          onUngueltig(false);
          onWert(null);
          return;
        }
        const sek = parseMinSek(roh);
        const ungueltig = !Number.isFinite(sek);
        setFehler(ungueltig);
        onUngueltig(ungueltig);
        if (!ungueltig) onWert(sek);
      }}
      sx={{ width: 150 }}
    />
  );
};

function statusChip(status: AdVideo['status']): {
  label: string;
  color: 'default' | 'success' | 'info' | 'warning' | 'error';
} {
  switch (status) {
    case 'uploading':
      return { label: 'Upload läuft', color: 'info' };
    case 'preparing':
      return { label: 'Wird aufbereitet', color: 'info' };
    case 'ready':
      return { label: 'Bereit', color: 'success' };
    case 'errored':
      return { label: 'Fehler', color: 'error' };
    case 'deleted':
      return { label: 'Gelöscht', color: 'default' };
    default:
      return { label: status, color: 'default' };
  }
}

// C8 (P3): 409 `video_in_benutzung` bringt die Verwendungen als Klartext mit (z. B. „wird am
// 2026-10-28 um 10:00 Uhr gebraucht") — die muss der Operator lesen, nicht den Code.
function inBenutzungText(err: unknown, video: { title: string }, fallback: string): string {
  const e = err as {
    response?: { data?: { error?: string; details?: unknown } };
    message?: string;
  };
  const data = e?.response?.data;
  if (data?.error === 'video_in_benutzung' && Array.isArray(data.details) && data.details.length) {
    return `Nicht möglich — „${video.title}“ ${data.details.map((d) => klartext(String(d))).join('; ')}.`;
  }
  if (
    typeof data?.error === 'string' &&
    data.error.startsWith('Default-Video kann nicht deaktiviert')
  ) {
    return `„${video.title}“ ist der Standard nach der Sendung und lässt sich nicht ausschalten — erst ein anderes Werbevideo als Standard einschalten.`;
  }
  return data?.error || e?.message || fallback;
}

// Die Begründungen kommen als Klartext vom Server (routes/live.ts videoReferenzen), tragen aber noch
// Technikwörter. Hier nur die Wörter tauschen — der Satz bleibt der des Servers.
function klartext(grund: string): string {
  return grund
    .replace(/Session (\d+)/g, 'Sendung Nr. $1')
    .replace('(Löschen bräche den Replay)', '(Löschen bräche die Aufzeichnung im Archiv)')
    .replace(
      'ist das Standard-Werbevideo — erst ein anderes als Standard markieren',
      'ist der Standard nach der Sendung — erst ein anderes Werbevideo als Standard einschalten',
    );
}

// „Standard“ und „Aktiv“ beantworten zwei verschiedene Fragen (Persona-Runde 1: Sandra hielt sie für
// dasselbe). Belegt im Code: der Standard ist der Rückfall von resolveAutoRollVideo, wenn der
// Sendeplan für den Tag nichts festlegt, und das einzige Video hinter Aufzeichnungen im Archiv
// (resolveDefaultAdVideo, GET /replays post_roll). „Aktiv“ ist die Abspielerlaubnis: jede
// Auflösung filtert auf is_active = 1, auch die Auswahl im Sendeplan.
const STANDARD_SATZ =
  'Standard = läuft nach der Sendung an jedem Tag, für den im Sendeplan kein anderes Werbevideo ausgewählt ist, und hinter Aufzeichnungen im Archiv auf live.finanztrends.de. Es gibt immer genau eins.';
const AKTIV_SATZ_WERBUNG =
  'Aktiv = das Video darf überhaupt laufen. Ausgeschaltet läuft es nirgends mehr, bleibt aber gespeichert und lässt sich wieder einschalten.';
const AKTIV_SATZ_SENDUNG =
  'Aktiv = das Video lässt sich im Sendeplan als Aufzeichnung für einen Termin wählen. Ausgeschaltet bleibt es gespeichert und lässt sich wieder einschalten.';

const AdVideos: FC = () => {
  const [videos, setVideos] = useState<AdVideo[]>([]);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [titleInput, setTitleInput] = useState('');
  // Was für ein Video wird hochgeladen. Default 'ad' — der Tab hiess bisher
  // „Werbe-Videos" und das ist weiterhin der häufige Fall; eine Sendung wählt
  // man bewusst.
  const [uploadKind, setUploadKind] = useState<'ad' | 'show'>('ad');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadingTitle, setUploadingTitle] = useState<string | null>(null);
  // Legacy-Preview-Modal-State — wird seit dem Switch auf Player-Tab nicht mehr
  // aufgemacht, aber Variablen bleiben damit kein Reference-Error entsteht.
  // TODO V2: kompletten Modal-Block + hls.js-Loader entfernen.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);
  // Schreib-Reads damit eslint die Setter nicht als unused flagged
  void setPreviewUrl;
  void setPreviewTitle;
  void previewTitle;

  // Per-Video-CTA-Editor state. CTAs gelten ausschließlich während Auto-Roll
  // dieses Videos (Live-Show-CTAs werden dann ignoriert).
  interface AdVideoCta {
    id?: number;
    display_order: number;
    button_label: string;
    button_url: string;
    time_from_sec: number;
    time_to_sec: number | null;
    is_active: boolean;
    _key: string;
    _error?: string;
  }
  const [ctaDialogVideoId, setCtaDialogVideoId] = useState<number | null>(null);
  const [ctaDialogVideoTitle, setCtaDialogVideoTitle] = useState<string>('');
  const [ctaDialogRows, setCtaDialogRows] = useState<AdVideoCta[]>([]);
  const [ctaDialogPrefillOk, setCtaDialogPrefillOk] = useState(false);
  const [ctaDialogBusy, setCtaDialogBusy] = useState(false);
  // Ungültige Zeit-Eingaben je Feld (`<_key>:ab` / `<_key>:bis`) — sperren das Speichern.
  const [zeitFehler, setZeitFehler] = useState<Record<string, boolean>>({});
  const zeitUngueltig = Object.values(zeitFehler).some(Boolean);
  // Pro-Video CTA-Counts für die Library-Tabelle. Wird parallel zu /admin/ad-videos
  // einmalig beim List-Load geholt damit User sofort sieht "3 CTAs / 0 CTAs".
  const [ctaCounts, setCtaCounts] = useState<Record<number, number>>({});
  // Angebot festlegen; `danach` wiederholt den Schritt, den der Server deswegen abgelehnt hat.
  const [angebotFrage, setAngebotFrage] = useState<{
    video: { id: number; title: string };
    meldung?: string;
    danach?: () => void;
  } | null>(null);
  // ID-Spalte und Technik-Hinweise nur für den Admin (Anzeige-Weiche, kein Rechte-Riegel).
  const istAdmin = useIstAdmin();

  const fetchVideos = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; videos: AdVideo[] }>(
        '/api/live/admin/ad-videos',
      );
      const list = res.data.videos || [];
      setVideos(list);
      // Counts in einer Map cachen — Library-Tabelle liest aus dieser, dialog
      // re-fetch holt frische CTAs beim Save zurück.
      const counts: Record<number, number> = {};
      for (const v of list) counts[v.id] = Number(v.cta_count) || 0;
      setCtaCounts(counts);
    } catch {
      /* network blip — keep last state */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await fetchVideos();
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [fetchVideos]);

  const handleFilePicked = async (file: File) => {
    const title = titleInput.trim() || file.name.replace(/\.[^.]+$/, '');
    if (!title) {
      setToast({ msg: 'Titel erforderlich', severity: 'error' });
      return;
    }
    setBusy(true);
    setUploadProgress(0);
    setUploadingTitle(title);
    try {
      // 1. Direct-Upload-URL vom Backend holen
      const createRes = await api.post<{
        success: boolean;
        id: number;
        upload_url: string;
        mux_upload_id: string;
      }>('/api/live/admin/ad-videos/upload-url', {
        title,
        cors_origin: window.location.origin,
        kind: uploadKind,
      });

      const uploadUrl = createRes.data.upload_url;
      const videoId = createRes.data.id;
      if (!uploadUrl) throw new Error('keine upload_url vom Backend');

      // 2. Datei direkt zu Mux, in Stücken. Vorher war das ein einzelner PUT:
      // bei einer Sendungsaufzeichnung von mehreren GB fängt der nach jedem
      // Netz-Hüpfer wieder bei 0 an. UpChunk stückelt und wiederholt pro Stück
      // — dasselbe Muster nutzt der Aufzeichnungen-Tab schon (Replays.tsx).
      await new Promise<void>((resolve, reject) => {
        const upload = UpChunk.createUpload({
          endpoint: uploadUrl,
          file,
          dynamicChunkSize: true,
        });
        upload.on('progress', (evt: { detail: number }) => {
          setUploadProgress(Math.round(evt.detail));
        });
        upload.on('success', () => resolve());
        upload.on('error', (evt: { detail?: { message?: string } }) => {
          const msg = evt?.detail?.message || 'Upload fehlgeschlagen';
          // Dem Backend Bescheid geben, sonst haengt die Zeile bis Mux' Fenster
          // ablaeuft auf „Upload läuft" — der Toast hier sieht nur der, der
          // gerade davorsitzt (Vorfall 2026-08-27). Best-effort: scheitert die
          // Meldung, raeumt der Abgleich im Listen-Endpunkt spaeter auf.
          if (videoId) {
            api
              .post(`/api/live/admin/ad-videos/${videoId}/upload-failed`, { message: msg })
              .catch(() => undefined);
          }
          reject(new Error(msg));
        });
      });

      setUploadProgress(100);
      setToast({
        msg: 'Hochgeladen — das Video wird jetzt aufbereitet (meist unter 1 Minute).',
        severity: 'success',
      });
      setTitleInput('');
      await fetchVideos();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setToast({
        msg: e?.response?.data?.error || e?.message || 'Upload fehlgeschlagen',
        severity: 'error',
      });
    } finally {
      setBusy(false);
      setUploadProgress(null);
      setUploadingTitle(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const setDefault = async (video: AdVideo) => {
    if (video.status !== 'ready' || !video.is_active) {
      setToast({
        msg: 'Standard kann nur ein fertiges, eingeschaltetes Werbevideo sein.',
        severity: 'error',
      });
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/live/admin/ad-videos/${video.id}/set-default`);
      await fetchVideos();
      setToast({
        msg: `„${video.title}“ läuft jetzt als Standard nach der Sendung.`,
        severity: 'success',
      });
    } catch (err) {
      const e = err as {
        response?: { data?: { error?: string; details?: string } };
        message?: string;
      };
      if (e?.response?.data?.error === 'ohne_angebot') {
        setAngebotFrage({
          video: { id: video.id, title: video.title },
          meldung: e.response.data.details,
          danach: () => void setDefault(video),
        });
        return;
      }
      setToast({
        msg: e?.response?.data?.error || e?.message || 'Standard konnte nicht gewechselt werden',
        severity: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (video: AdVideo) => {
    setBusy(true);
    try {
      await api.patch(`/api/live/admin/ad-videos/${video.id}`, {
        is_active: !video.is_active,
      });
      await fetchVideos();
    } catch (err) {
      setToast({ msg: inBenutzungText(err, video, 'Update fehlgeschlagen'), severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  // Umbenennen: PATCH /api/live/admin/ad-videos/:id nimmt `title` (routes/live.ts, leerer Titel
  // → 400, vom Wiederholungs-Abgleich gepflegte Videos → 409). Persona-Runde 1: Titel wie
  // „KG-040926“ versteht niemand, und bisher ließ er sich nach dem Hochladen nicht mehr ändern.
  const [umbenennen, setUmbenennen] = useState<AdVideo | null>(null);
  const [neuerTitel, setNeuerTitel] = useState('');
  const [umbenennenLaeuft, setUmbenennenLaeuft] = useState(false);

  const titelSpeichern = async () => {
    if (!umbenennen) return;
    const t = neuerTitel.trim();
    if (!t) return;
    setUmbenennenLaeuft(true);
    try {
      await api.patch(`/api/live/admin/ad-videos/${umbenennen.id}`, { title: t });
      await fetchVideos();
      setToast({ msg: `Umbenannt in „${t}“.`, severity: 'success' });
      setUmbenennen(null);
    } catch (err) {
      setToast({
        msg: inBenutzungText(err, umbenennen, 'Umbenennen fehlgeschlagen'),
        severity: 'error',
      });
    } finally {
      setUmbenennenLaeuft(false);
    }
  };

  // Löschen läuft über GefahrBestaetigung mit Tipp-Bestätigung (Entscheidung Till 28.10.: Löschen
  // bleibt für alle, „Aktiv" ausschalten ist der normale Weg). Ein 409 „wird noch gebraucht"
  // bleibt im Dialog stehen, damit man den Grund liest.
  const [loeschKandidat, setLoeschKandidat] = useState<AdVideo | null>(null);
  const [loeschFehler, setLoeschFehler] = useState<string | null>(null);
  const [loeschLaeuft, setLoeschLaeuft] = useState(false);

  const deleteVideo = async (video: AdVideo) => {
    setLoeschLaeuft(true);
    setLoeschFehler(null);
    setBusy(true);
    try {
      await api.delete(`/api/live/admin/ad-videos/${video.id}`);
      await fetchVideos();
      setLoeschKandidat(null);
      setToast({ msg: `„${video.title}“ ist endgültig gelöscht.`, severity: 'success' });
    } catch (err) {
      setLoeschFehler(inBenutzungText(err, video, 'Löschen fehlgeschlagen'));
    } finally {
      setLoeschLaeuft(false);
      setBusy(false);
    }
  };

  // ── CTA-Dialog ────────────────────────────────────────────────
  const openCtaDialog = (video: AdVideo) => {
    setCtaDialogVideoId(video.id);
    setCtaDialogVideoTitle(video.title);
    // Prefill leer + ok=false; useEffect lädt CTAs gleich nach
    setCtaDialogRows([]);
    setCtaDialogPrefillOk(false);
    setZeitFehler({});
  };

  useEffect(() => {
    if (ctaDialogVideoId == null) return;
    let cancelled = false;
    api
      .get<{ success: boolean; ctas: Omit<AdVideoCta, '_key' | '_error'>[] }>(
        `/api/live/admin/ad-videos/${ctaDialogVideoId}/ctas`,
      )
      .then((res) => {
        if (cancelled) return;
        const rows = (res.data.ctas || []).map((c, i) => ({
          ...c,
          _key: `existing-${c.id ?? i}-${Math.random().toString(36).slice(2)}`,
        }));
        setCtaDialogRows(rows);
        setCtaDialogPrefillOk(true);
      })
      .catch(() => {
        if (cancelled) return;
        setCtaDialogRows([]);
        setCtaDialogPrefillOk(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ctaDialogVideoId]);

  const addCtaDialogRow = () => {
    setCtaDialogRows((prev) => {
      if (prev.length >= 3) return prev;
      const lastEnd = prev.reduce((max, c) => {
        if (c.time_to_sec === null) return Math.max(max, c.time_from_sec + 1);
        return Math.max(max, c.time_to_sec);
      }, 0);
      return [
        ...prev,
        {
          display_order: prev.length + 1,
          button_label: '',
          button_url: '',
          time_from_sec: lastEnd,
          time_to_sec: null,
          is_active: true,
          _key: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        },
      ];
    });
  };
  const updateCtaDialogRow = (idx: number, patch: Partial<AdVideoCta>) => {
    setCtaDialogRows((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, ...patch, _error: undefined } : c)),
    );
  };
  const removeCtaDialogRow = (idx: number) => {
    const weg = ctaDialogRows[idx]?._key;
    setCtaDialogRows((prev) => prev.filter((_, i) => i !== idx));
    if (weg) {
      setZeitFehler((prev) => {
        const rest = { ...prev };
        delete rest[`${weg}:ab`];
        delete rest[`${weg}:bis`];
        return rest;
      });
    }
  };

  const ctaRowsWithErrors = useMemo<AdVideoCta[]>(() => {
    const errors: Record<number, string> = {};
    const active = ctaDialogRows.map((c, i) => ({ ...c, _idx: i })).filter((c) => c.is_active);
    for (let i = 0; i < active.length; i++) {
      for (let j = i + 1; j < active.length; j++) {
        const a = active[i];
        const b = active[j];
        const aTo = a.time_to_sec === null ? Number.POSITIVE_INFINITY : a.time_to_sec;
        const bTo = b.time_to_sec === null ? Number.POSITIVE_INFINITY : b.time_to_sec;
        if (a.time_from_sec < bTo && b.time_from_sec < aTo) {
          const msg = 'Überschneidet sich zeitlich mit einem anderen eingeschalteten Knopf';
          errors[a._idx] = msg;
          errors[b._idx] = msg;
        }
      }
    }
    return ctaDialogRows.map((c, i) => ({ ...c, _error: errors[i] }));
  }, [ctaDialogRows]);

  const handleCtaDialogSave = async () => {
    if (ctaDialogVideoId == null || !ctaDialogPrefillOk || zeitUngueltig) return;
    // Partial-Edit-Schutz: Wenn eine Row label ODER url hat aber nicht beides,
    // reject mit Toast statt stillschweigend rauszufiltern. Sonst würde ein
    // versehentliches "URL leeren während Edit" → Save → bulk-replace die Row
    // unbemerkt aus der DB löschen.
    const partial = ctaDialogRows.findIndex((c) => {
      const hasLabel = c.button_label.trim().length > 0;
      const hasUrl = c.button_url.trim().length > 0;
      return hasLabel !== hasUrl; // XOR: genau eines ist gefüllt
    });
    if (partial !== -1) {
      setToast({
        msg: `Knopf ${partial + 1}: Text und Link gehören zusammen — beide ausfüllen oder beide leeren.`,
        severity: 'error',
      });
      return;
    }
    // Komplett-leere Rows (User klickte Add und entschied sich anders) werden
    // weiterhin stillschweigend rausgefiltert — kein Save-Block dafür.
    const validRows = ctaDialogRows.filter(
      (c) => c.button_label.trim().length > 0 && c.button_url.trim().length > 0,
    );
    const ctasPayload = validRows.map((c, i) => ({
      display_order: i + 1,
      button_label: c.button_label.trim(),
      button_url: c.button_url.trim(),
      time_from_sec: c.time_from_sec,
      time_to_sec: c.time_to_sec,
      is_active: c.is_active,
    }));
    setCtaDialogBusy(true);
    try {
      // Nimmt das Speichern einem benutzten Video das letzte Angebot, fragt der Server nach.
      for (let trotzdem = false; ; trotzdem = true) {
        try {
          await api.post(`/api/live/admin/ad-videos/${ctaDialogVideoId}/ctas`, {
            ctas: ctasPayload,
            ...(trotzdem ? { trotzdem: true } : {}),
          });
          break;
        } catch (err) {
          const data = (err as { response?: { data?: { error?: string; details?: string } } })
            ?.response?.data;
          if (
            trotzdem ||
            data?.error !== 'angebot_wird_entfernt' ||
            !window.confirm(`${data.details}\n\nTrotzdem ohne Angebot speichern?`)
          ) {
            throw err;
          }
        }
      }
      setToast({
        msg:
          ctasPayload.length === 1
            ? '1 Knopf gespeichert'
            : `${ctasPayload.length} Knöpfe gespeichert`,
        severity: 'success',
      });
      setCtaDialogVideoId(null);
      await fetchVideos();
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setToast({
        msg: e?.response?.data?.error || e?.message || 'Speichern fehlgeschlagen',
        severity: 'error',
      });
    } finally {
      setCtaDialogBusy(false);
    }
  };

  const openPreview = async (video: AdVideo) => {
    if (!video.hls_url || video.status !== 'ready') {
      setToast({ msg: 'Video ist noch nicht bereit', severity: 'error' });
      return;
    }
    // Open-first-Pattern gegen Browser-Popup-Blocker: window.open MUSS synchron
    // innerhalb des Click-Handlers gerufen werden, sonst löst Chrome/Safari den
    // Popup-Schutz aus und blockt den neuen Tab silent. Wir öffnen also zuerst
    // mit about:blank, holen dann den State, und setzen .location nach.
    //
    // KRITISCH: KEIN `noopener`-Flag — Chrome returnt dann IMMER null als
    // Window-Handle (auch wenn Popup erfolgreich öffnet), und wir könnten
    // nicht mehr `win.location.href` setzen. Wir öffnen zu live.finanztrends.de
    // das wir kontrollieren, kein Tabnabbing-Risiko.
    //
    // Player läuft auf live.finanztrends.de (separates Subdomain, separater
    // Cookie-Scope). Statt den Player /admin/preview-status cross-domain
    // aufrufen zu lassen (cookie wäre nicht sichtbar → 401), holen wir den
    // synthesized state hier auf db.finanztrends.de (cookie ist da) und
    // packen ihn als base64-Inline-State in die Player-URL.
    const win = window.open('about:blank', '_blank');
    if (!win) {
      setToast({
        msg: 'Vorschau-Tab wurde vom Browser geblockt — bitte Popups für diese Seite erlauben',
        severity: 'error',
      });
      return;
    }
    try {
      const res = await api.get<Record<string, unknown>>(
        `/api/live/admin/preview-status?ad_video_id=${video.id}`,
      );
      // base64-encode UTF-8-safe JSON: encodeURIComponent → unescape → btoa
      const json = JSON.stringify(res.data);
      const encoded = btoa(unescape(encodeURIComponent(json)));
      const url = `https://live.finanztrends.de/?preview_inline=${encodeURIComponent(encoded)}`;
      win.location.href = url;
    } catch (err) {
      // Fetch fehlgeschlagen — placeholder-Tab schließen damit User keinen
      // about:blank-Müll-Tab hat
      try {
        win.close();
      } catch {
        /* noop */
      }
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setToast({
        msg: e?.response?.data?.error || e?.message || 'Vorschau konnte nicht geladen werden',
        severity: 'error',
      });
    }
  };

  // hls.js lazy-load für Preview-Modal (mirrors Replays.tsx pattern)
  useEffect(() => {
    if (!previewUrl) return;
    let cancelled = false;
    interface HlsInstance {
      loadSource: (url: string) => void;
      attachMedia: (video: HTMLVideoElement) => void;
      destroy: () => void;
    }
    interface HlsCtor {
      new (): HlsInstance;
      isSupported: () => boolean;
    }
    let hls: HlsInstance | null = null;
    const HLS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.15/dist/hls.min.js';
    const loadHls = (): Promise<HlsCtor | null> => {
      const w = window as unknown as { Hls?: HlsCtor };
      if (w.Hls) return Promise.resolve(w.Hls);
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = HLS_CDN;
        script.async = true;
        script.onload = () => resolve((window as unknown as { Hls?: HlsCtor }).Hls || null);
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
      });
    };
    void (async () => {
      const Hls = await loadHls();
      if (cancelled || !previewVideoRef.current) return;
      if (Hls && Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(previewUrl);
        hls.attachMedia(previewVideoRef.current);
      } else if (previewVideoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        previewVideoRef.current.src = previewUrl;
      }
    })();
    return () => {
      cancelled = true;
      if (hls) {
        try {
          hls.destroy();
        } catch {
          /* noop */
        }
      }
    };
  }, [previewUrl]);

  const kopfZelle = { backgroundColor: '#e0e0e0 !important', fontWeight: 'bold' } as const;

  // Eine Tabelle je Verwendung. Werbevideos tragen „Standard“ und den Knopf im Video; eine
  // Aufzeichnung braucht beides nicht (set-default lehnt kind='show' ab, Video-Knöpfe laufen nur
  // während des Werbevideos nach der Sendung bzw. nach einer Aufzeichnung im Archiv).
  const renderTabelle = (liste: AdVideo[], art: 'ad' | 'show') => {
    const istWerbung = art === 'ad';
    const spalten = 6 + (istAdmin ? 1 : 0) + (istWerbung ? 2 : 0);
    return (
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {istAdmin && <TableCell sx={kopfZelle}>ID</TableCell>}
              <TableCell sx={kopfZelle}>Titel</TableCell>
              <TableCell sx={kopfZelle}>Status</TableCell>
              <TableCell sx={kopfZelle}>Dauer</TableCell>
              <TableCell sx={{ ...kopfZelle, whiteSpace: 'nowrap' }}>Hochgeladen</TableCell>
              {istWerbung && (
                <TableCell sx={kopfZelle}>
                  <Tooltip arrow placement="top" title={STANDARD_SATZ}>
                    <span style={{ cursor: 'help' }}>Standard</span>
                  </Tooltip>
                </TableCell>
              )}
              <TableCell sx={kopfZelle}>
                <Tooltip
                  arrow
                  placement="top"
                  title={istWerbung ? AKTIV_SATZ_WERBUNG : AKTIV_SATZ_SENDUNG}
                >
                  <span style={{ cursor: 'help' }}>Aktiv</span>
                </Tooltip>
              </TableCell>
              {istWerbung && <TableCell sx={kopfZelle}>Knöpfe im Video</TableCell>}
              <TableCell sx={kopfZelle} align="right">
                Aktionen
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {liste.length === 0 && (
              <TableRow>
                <TableCell colSpan={spalten} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {istWerbung
                    ? 'Noch kein Werbevideo hochgeladen.'
                    : 'Noch kein Sendungsvideo hochgeladen.'}
                </TableCell>
              </TableRow>
            )}
            {liste.map((v) => {
              const chip = statusChip(v.status);
              const isReady = v.status === 'ready' && !!v.is_active;
              // Vom Wiederholungs-Abgleich gepflegt: der Server lehnt Aenderungen mit 409 ab,
              // die Knoepfe sind deshalb gesperrt statt in einen Fehler zu laufen.
              const verwaltet = v.managed_source_webinar_id != null;
              const verwaltetHinweis =
                'Legt der Wiederholungs-Abgleich automatisch an und pflegt es selbst.';
              const standardHinweis = v.is_default
                ? 'Ist der Standard. Zum Wechseln bei einem anderen Werbevideo einschalten.'
                : !isReady
                  ? 'Standard kann nur ein fertiges, eingeschaltetes Werbevideo sein.'
                  : 'Als Standard nach der Sendung festlegen';
              return (
                <TableRow key={v.id} hover>
                  {istAdmin && <TableCell>{v.id}</TableCell>}
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: v.is_default ? 700 : 400 }}>
                      {v.title}
                    </Typography>
                    {istWerbung && !!v.is_default && (
                      <Tooltip
                        arrow
                        placement="top"
                        title="Läuft an Tagen ohne eigene Auswahl im Sendeplan direkt nach der Sendung."
                      >
                        <Chip
                          size="small"
                          color="success"
                          sx={{ mt: 0.5, mr: 0.5, cursor: 'help' }}
                          label="Standard nach der Sendung"
                        />
                      </Tooltip>
                    )}
                    {verwaltet && (
                      <Tooltip title={verwaltetHinweis}>
                        <Chip
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                          label={`Automatisch: Wiederholung ${v.managed_source_webinar_id}${
                            v.rerun_current_for
                              ? ` · Werbevideo am ${String(v.rerun_current_for)
                                  .slice(0, 10)
                                  .split('-')
                                  .reverse()
                                  .join('.')}`
                              : ''
                          }`}
                        />
                      </Tooltip>
                    )}
                    {v.status_error && (
                      <Tooltip title={v.status_error}>
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                          {v.status_error.slice(0, 80)}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={chip.label} color={chip.color} size="small" />
                    {v.status === 'preparing' && (
                      <CircularProgress size={12} sx={{ ml: 1, verticalAlign: 'middle' }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDuration(v.duration_sec)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {v.created_by_email ? (
                      <Tooltip
                        arrow
                        placement="top"
                        title={`Hochgeladen von ${v.created_by_email}`}
                      >
                        <span style={{ cursor: 'help' }}>{fmtDatum(v.created_at)}</span>
                      </Tooltip>
                    ) : (
                      fmtDatum(v.created_at)
                    )}
                  </TableCell>
                  {istWerbung && (
                    <TableCell>
                      <Tooltip
                        arrow
                        placement="top"
                        title={verwaltet ? verwaltetHinweis : standardHinweis}
                      >
                        <span>
                          <Switch
                            checked={!!v.is_default}
                            onChange={() => setDefault(v)}
                            disabled={busy || !isReady || !!v.is_default || verwaltet}
                            size="small"
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>
                    <Switch
                      checked={!!v.is_active}
                      onChange={() => toggleActive(v)}
                      disabled={busy || v.status === 'deleted' || verwaltet}
                      size="small"
                    />
                  </TableCell>
                  {istWerbung && (
                    <TableCell>
                      <Tooltip
                        arrow
                        placement="top"
                        title="Knöpfe, die während dieses Werbevideos unter dem Video erscheinen (höchstens 3)"
                      >
                        <span>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<TouchAppIcon fontSize="small" />}
                            onClick={() => openCtaDialog(v)}
                            disabled={v.status === 'deleted' || verwaltet}
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                          >
                            {ctaCounts[v.id] ?? 0} von 3 · bearbeiten
                          </Button>
                        </span>
                      </Tooltip>
                      {v.status === 'ready' && !!v.is_active && (
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                          <AngebotLageChip lage={v.angebot_lage} />
                          {lageFehlt(v.angebot_lage) && !verwaltet && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() =>
                                setAngebotFrage({ video: { id: v.id, title: v.title } })
                              }
                              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                            >
                              Angebot festlegen
                            </Button>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {/* Text statt Symbol (Persona-Runde 1: ▷ und Mülleimer ohne Beschriftung). */}
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Tooltip title="Öffnet den Player in einem neuen Tab">
                        <span>
                          <Button
                            size="small"
                            startIcon={<PlayCircleOutlineIcon fontSize="small" />}
                            onClick={() => openPreview(v)}
                            disabled={!isReady}
                            sx={{ textTransform: 'none' }}
                          >
                            Vorschau
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={verwaltet ? verwaltetHinweis : 'Titel ändern'}>
                        <span>
                          <Button
                            size="small"
                            startIcon={<EditOutlinedIcon fontSize="small" />}
                            onClick={() => {
                              setNeuerTitel(v.title);
                              setUmbenennen(v);
                            }}
                            disabled={busy || v.status === 'deleted' || verwaltet}
                            sx={{ textTransform: 'none' }}
                          >
                            Umbenennen
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          verwaltet
                            ? verwaltetHinweis
                            : 'Endgültig löschen — der normale Weg ist „Aktiv“ ausschalten'
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                            onClick={() => {
                              setLoeschFehler(null);
                              setLoeschKandidat(v);
                            }}
                            disabled={busy || v.status === 'deleted' || verwaltet}
                            sx={{ textTransform: 'none' }}
                          >
                            Löschen
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const aufzeichnungen = videos.filter((v) => v.kind === 'show');
  const werbevideos = videos.filter((v) => v.kind !== 'show');

  return (
    <Box sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Videos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Alle hochgeladenen Videos, getrennt nach Verwendung. Wird ein Video nicht mehr gebraucht,
        einfach „Aktiv“ ausschalten — dann läuft es nicht mehr, bleibt aber gespeichert.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Neues Video hochladen
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
          Erst auswählen, wofür das Video ist — danach landet es im passenden Bereich unten.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <FormControl size="small" sx={{ minWidth: 300 }} disabled={busy}>
            <InputLabel>Wofür ist das Video?</InputLabel>
            <Select
              label="Wofür ist das Video?"
              value={uploadKind}
              onChange={(e) => setUploadKind(e.target.value as 'ad' | 'show')}
            >
              <MenuItem value="ad">Werbevideo — läuft nach der Sendung</MenuItem>
              <MenuItem value="show">Sendungsvideo — läuft statt einer Live-Sendung</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Titel (leer = Dateiname)"
            value={titleInput}
            onChange={(e) => setTitleInput(e.target.value)}
            size="small"
            fullWidth
            disabled={busy}
            inputProps={{ maxLength: 255 }}
            helperText="Ein sprechender Titel, z. B. „Kursgespräch 04.09. – Nvidia“ — damit andere das Video später wiederfinden. Lässt sich danach mit „Umbenennen“ ändern."
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFilePicked(f);
            }}
          />
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            sx={{ minWidth: 160 }}
          >
            Video hochladen
          </Button>
        </Stack>
        {uploadProgress != null && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              {uploadingTitle} — {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
      </Paper>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Sendungsvideos — laufen statt einer Live-Sendung
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Fertig produzierte Sendungen, die zum geplanten Termin statt einer Live-Sendung für alle
          Zuschauer gleichzeitig laufen. Im Sendeplan heißt diese Sendungsart „Aufzeichnung“ — dort
          beim Termin auswählen. Danach folgt wie bei einer Live-Sendung das Werbevideo.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Vergangene Live-Sendungen findest du im Reiter „Aufzeichnungen“ — sie stehen nicht hier.
          Eine vergangene Live-Sendung lässt sich nicht direkt als Sendungsvideo übernehmen. Der
          Umweg: im Reiter „Aufzeichnungen“ mit „Download-Datei erstellen“ die Videodatei
          herunterladen und sie hier als Sendungsvideo hochladen.
        </Typography>
        {renderTabelle(aufzeichnungen, 'show')}
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Werbevideos nach der Sendung
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Laufen direkt nach dem Ende der Sendung im selben Player weiter, mit einem Knopf zur
          Bestellseite. Welches läuft, legt der Sendeplan je Tag fest.
        </Typography>
        {/* Die beiden Schalter nebeneinander erklärt (Persona-Runde 1). */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#ffffff' }}>
            <Typography variant="subtitle2">Schalter „Standard“</Typography>
            <Typography variant="body2" color="text.secondary">
              Läuft nach der Sendung an jedem Tag, für den im Sendeplan kein anderes Werbevideo
              ausgewählt ist — und hinter Aufzeichnungen im Archiv auf live.finanztrends.de. Es gibt
              immer genau eins; erkennbar am Kennzeichen „Standard nach der Sendung“.
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#ffffff' }}>
            <Typography variant="subtitle2">Schalter „Aktiv“</Typography>
            <Typography variant="body2" color="text.secondary">
              Ob das Video überhaupt laufen darf. Ausgeschaltet läuft es nirgends mehr, bleibt aber
              gespeichert und lässt sich jederzeit wieder einschalten. Der Standard muss aktiv sein.
            </Typography>
          </Paper>
        </Box>
        {renderTabelle(werbevideos, 'ad')}
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Löschen ist endgültig.</strong> Der normale Weg ist „Aktiv“ ausschalten: das Video
        läuft dann nicht mehr und lässt sich jederzeit wieder einschalten. Ein Video, das noch im
        Sendeplan steht, gerade läuft oder der Standard ist, lässt sich weder ausschalten noch
        löschen — die Meldung nennt dann den Grund.
      </Alert>

      <NurAdmin bereich titel="Technik: Werbevideo-Ablauf">
        <Typography variant="caption" color="text.secondary" component="div">
          Nach manuellem End-Stream schaltet der Player nahtlos auf das aufgelöste Video
          (Sendeplan-Auswahl → Wiederholungs-Zeiger → Standard). Das LIVE-Badge bleibt sichtbar.
          Videos liegen bei Mux; Löschen entfernt das Mux-Asset und setzt die Zeile auf „deleted“.
        </Typography>
      </NurAdmin>

      <GefahrBestaetigung
        open={loeschKandidat !== null}
        titel={`„${loeschKandidat?.title ?? ''}“ endgültig löschen?`}
        wirkung={
          <>
            Das Video und alle seine Dateien werden beim Videodienst gelöscht — das lässt sich nicht
            rückgängig machen. Frühere Sendungen behalten in der Statistik nur den Titel.
            <br />
            <br />
            Soll das Video nur nicht mehr laufen? Dann hier abbrechen und in der Liste „Aktiv“
            ausschalten. Unsicher? {TECHNIK_KONTAKT} fragen.
          </>
        }
        knopfText="Endgültig löschen"
        bestaetigungsWort="LÖSCHEN"
        laeuft={loeschLaeuft}
        fehler={loeschFehler}
        onBestaetigen={() => {
          if (loeschKandidat) void deleteVideo(loeschKandidat);
        }}
        onAbbrechen={() => {
          setLoeschKandidat(null);
          setLoeschFehler(null);
        }}
      />
      {/* Knopf-Editor (Backend: Video-CTAs) — 0-3 Knöpfe pro Werbevideo, Zeitfenster in Sekunden ab
          Beginn des Werbevideos. Während des Werbevideos greifen ausschließlich diese. */}
      <Dialog
        open={ctaDialogVideoId !== null}
        onClose={() => !ctaDialogBusy && setCtaDialogVideoId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Knopf im Werbevideo „{ctaDialogVideoTitle}“</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Während dieses Werbevideo nach der Sendung läuft, erscheint unter dem Video ein Knopf —
            meist zur Bestellseite. Bis zu 3 Knöpfe nacheinander sind möglich. Die Zeiten zählen in
            Minuten:Sekunden ab Beginn des Werbevideos: „ab 0:00“ heißt sofort, „bis“ leer heißt bis
            zum Ende des Videos. Beispiel: „ab 42:30“ = 42 Minuten und 30 Sekunden nach dem Start.
          </Alert>
          {!ctaDialogPrefillOk && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Die Knöpfe konnten nicht geladen werden — Speichern ist gesperrt, damit nichts
              verloren geht. Dialog schließen und neu öffnen.
            </Alert>
          )}
          <Stack spacing={2}>
            {ctaRowsWithErrors.map((c, i) => (
              <Paper key={c._key} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 24 }}>
                    Knopf {i + 1}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={c.is_active}
                        onChange={(e) => updateCtaDialogRow(i, { is_active: e.target.checked })}
                      />
                    }
                    label="Eingeschaltet"
                    sx={{ ml: 1 }}
                  />
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                    onClick={() => removeCtaDialogRow(i)}
                    disabled={ctaDialogBusy}
                    sx={{ textTransform: 'none' }}
                  >
                    Knopf entfernen
                  </Button>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <TextField
                    label="Text auf dem Knopf"
                    size="small"
                    value={c.button_label}
                    onChange={(e) => updateCtaDialogRow(i, { button_label: e.target.value })}
                    inputProps={{ maxLength: 255 }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Link beim Klick (https://…)"
                    size="small"
                    value={c.button_url}
                    onChange={(e) => updateCtaDialogRow(i, { button_url: e.target.value })}
                    inputProps={{ maxLength: 2000 }}
                    sx={{ flex: 1.5 }}
                  />
                </Stack>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ mt: 1.5 }}
                  alignItems="flex-start"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <ZeitFeld
                    label="ab (Min:Sek)"
                    wert={c.time_from_sec}
                    onWert={(sek) => updateCtaDialogRow(i, { time_from_sec: sek ?? 0 })}
                    onUngueltig={(u) => setZeitFehler((prev) => ({ ...prev, [`${c._key}:ab`]: u }))}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                    →
                  </Typography>
                  <ZeitFeld
                    label="bis (Min:Sek)"
                    wert={c.time_to_sec}
                    leerErlaubt
                    leerText="bis Ende"
                    onWert={(sek) => updateCtaDialogRow(i, { time_to_sec: sek })}
                    onUngueltig={(u) =>
                      setZeitFehler((prev) => ({ ...prev, [`${c._key}:bis`]: u }))
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ pt: 1.25 }}>
                    {c.time_to_sec === null
                      ? `sichtbar ab ${fmtDuration(c.time_from_sec)} bis zum Ende des Videos`
                      : `sichtbar von ${fmtDuration(c.time_from_sec)} bis ${fmtDuration(c.time_to_sec)} (${fmtDuration(Math.max(0, c.time_to_sec - c.time_from_sec))} lang)`}
                  </Typography>
                </Stack>
                {c._error && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    {c._error}
                  </Typography>
                )}
              </Paper>
            ))}
            {ctaDialogRows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Noch kein Knopf — während dieses Werbevideos erscheint dann kein Knopf.
              </Typography>
            )}
            {ctaDialogRows.length < 3 && (
              <Button
                startIcon={<AddIcon />}
                onClick={addCtaDialogRow}
                disabled={ctaDialogBusy}
                size="small"
                sx={{ alignSelf: 'flex-start' }}
              >
                Knopf hinzufügen
              </Button>
            )}
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" component="div">
            Zwei eingeschaltete Knöpfe dürfen sich zeitlich nicht überschneiden. Die Klicks stehen
            in der Statistik unter „Klicks Sendung / Werbevideo“ (rechte Zahl).
          </Typography>
          <NurAdmin>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              Technik: Klicks landen in live_cta_clicks mit phase=auto-roll und cta_source=video.
            </Typography>
          </NurAdmin>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCtaDialogVideoId(null)} disabled={ctaDialogBusy}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleCtaDialogSave}
            disabled={
              ctaDialogBusy ||
              !ctaDialogPrefillOk ||
              zeitUngueltig ||
              ctaRowsWithErrors.some((c) => !!c._error)
            }
          >
            {ctaDialogBusy ? 'Speichere …' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={umbenennen !== null}
        onClose={() => !umbenennenLaeuft && setUmbenennen(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Video umbenennen</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Neuer Titel"
            value={neuerTitel}
            onChange={(e) => setNeuerTitel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && neuerTitel.trim()) void titelSpeichern();
            }}
            inputProps={{ maxLength: 255 }}
            helperText="Sprechend, z. B. „Kursgespräch 04.09. – Nvidia“ — damit andere das Video wiederfinden."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUmbenennen(null)} disabled={umbenennenLaeuft}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={() => void titelSpeichern()}
            disabled={
              umbenennenLaeuft || !neuerTitel.trim() || neuerTitel.trim() === umbenennen?.title
            }
          >
            {umbenennenLaeuft ? 'Speichere …' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <AngebotDialog
        video={angebotFrage?.video ?? null}
        meldung={angebotFrage?.meldung}
        onFertig={(ergebnis) => {
          const frage = angebotFrage;
          setAngebotFrage(null);
          void fetchVideos();
          if (ergebnis !== 'abgebrochen') frage?.danach?.();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.severity === 'error' ? 10000 : 5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default AdVideos;
